import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { TableNew } from '@/components-new/TableNew/TableNew';
import { TableHeadNew } from '@/components-new/TableNew/TableHeadNew';
import { TableHeadCellNew } from '@/components-new/TableNew/TableHeadCellNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { copyToClipboard, showSnackbarResponseError } from '@/helpers/helpers';
import { InvoiceCheckType } from '@/types/invoiceVaultTypes';
import { InvoiceCheckActionType } from '@/api/v1/organization/invoice-vault/invoiceCheckActionApi';
import { INVOICE_CHECK_STATUS_MAP } from '../../../const/const';
import { runInvoiceCheckAction } from '../../../api/api';
import { StatusCell } from '../../shared/StatusCell/StatusCell';
import { TableBodyNew, TableCellNew, TableRowNew } from '@/components-new/TableNew/styles';
import { COLORS } from '@/styles/colors';

type PropsType = {
  list: InvoiceCheckType[];
  isLoading: boolean;
  onChanged: () => void;
  onUnmask: (check: InvoiceCheckType) => void;
  onVerification: (check: InvoiceCheckType) => void;
};

export const InvoiceChecksList = ({ list = [], isLoading, onChanged, onUnmask, onVerification }: PropsType) => {
  const [confirm, setConfirm] = useState<{ check: InvoiceCheckType; action: InvoiceCheckActionType } | null>(null);

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: InvoiceCheckActionType }) => runInvoiceCheckAction(id, action),
    onSuccess(response) {
      if (response.error) return showSnackbarResponseError(response.error);

      setConfirm(null);
      onChanged();
    },
  });

  return (
    <>
      <TableNew
        isLoading={isLoading}
        noItems='No invoice checks yet.'
        itemsCount={list.length}
        header={
          <Box display='flex' justifyContent='space-between'>
            <Typography fontWeight='semibold'>Check Invoice requests</Typography>
            <Typography variant='bodySmall' marginBottom='8px'>
              Revoke until the vendor verifies. Every open is logged.
            </Typography>
          </Box>
        }
      >
        <TableHeadNew>
          <TableHeadCellNew name='vendor_name'>VENDOR</TableHeadCellNew>
          <TableHeadCellNew name='invoice_number'>INVOICE</TableHeadCellNew>
          <TableHeadCellNew name='amount'>AMOUNT</TableHeadCellNew>
          <TableHeadCellNew name='mode'>TYPE · MODE</TableHeadCellNew>
          <TableHeadCellNew name='expires_at'>SEND · EXPIRES</TableHeadCellNew>
          <TableHeadCellNew name='status' width='15%'>
            STATUS
          </TableHeadCellNew>

          <TableHeadCellNew name='actions' />
        </TableHeadNew>
        <TableBodyNew>
          {list.map((check) => (
            <TableRowNew key={check.id}>
              <TableCellNew>
                <Typography fontWeight='medium'>{check.vendor_name}</Typography>
                <Typography variant='caption'>{check.vendor_contact}</Typography>
              </TableCellNew>
              <TableCellNew>
                <Typography variant='bodySmall'>{check.invoice_number}</Typography>
                <Typography variant='caption'>
                  {check.bank_name_on_invoice} ••{check.account_last4_on_invoice}
                </Typography>
              </TableCellNew>
              <TableCellNew>{check.amount_label}</TableCellNew>
              <TableCellNew>
                <Typography variant='bodySmall' fontWeight='medium'>
                  {check.template_label}
                </Typography>
                <Typography variant='bodySmall2'>{check.mode_label}</Typography>
              </TableCellNew>
              <TableCellNew>
                {formatDate(check.expires_at, DATE_TIME_FORMAT)} · {check.expiry_label}
              </TableCellNew>
              <TableCellNew>
                <StatusCell
                  label={check.status_label}
                  variant={INVOICE_CHECK_STATUS_MAP[check.status]}
                  stamp={check.stamp}
                />
                {check.status === 'mismatch' && (
                  <Typography variant='bodySmall' fontWeight='medium' marginTop='4px' color={COLORS.red[450]}>
                    Transfer blocked. {check.result_detail}
                  </Typography>
                )}
              </TableCellNew>
              <TableCellNew>
                <Box display='flex' gap='20px' justifyContent='flex-end' flexWrap='wrap'>
                  {check.can_unmask && (
                    <ButtonNew size='medium' variant='secondary' onClick={() => onUnmask(check)}>
                      Unmask details
                    </ButtonNew>
                  )}
                  {check.has_verification && (
                    <ButtonNew size='medium' variant='secondary' onClick={() => onVerification(check)}>
                      Verification
                    </ButtonNew>
                  )}
                  {check.can_resend && (
                    <ButtonNew
                      size='medium'
                      variant='secondary'
                      onClick={() => setConfirm({ check, action: 'resend' })}
                    >
                      Resend
                    </ButtonNew>
                  )}
                  {check.can_revoke && (
                    <ButtonNew size='medium' variant='danger' onClick={() => setConfirm({ check, action: 'revoke' })}>
                      Revoke
                    </ButtonNew>
                  )}
                  {check.can_revoke && (
                    <ButtonNew
                      size='medium'
                      variant='secondary'
                      onClick={() => copyToClipboard(check.url, 'Link copied successfully.')}
                    >
                      Copy link
                    </ButtonNew>
                  )}
                  {!check.archived && !check.can_revoke && (
                    <ButtonNew
                      size='medium'
                      variant='secondary'
                      onClick={() => act.mutate({ id: check.id, action: 'archive' })}
                    >
                      Archive
                    </ButtonNew>
                  )}
                </Box>
              </TableCellNew>
            </TableRowNew>
          ))}
        </TableBodyNew>
      </TableNew>

      {confirm && (
        <ConfirmModal
          isOpen
          header={confirm.action === 'revoke' ? 'Revoke this request?' : 'Resend this request?'}
          body={
            confirm.action === 'revoke'
              ? `The link sent to ${confirm.check.vendor_name} stops working immediately.`
              : `A new link goes to ${confirm.check.vendor_contact}; the old one stops working.`
          }
          confirmText={confirm.action === 'revoke' ? 'Revoke' : 'Resend'}
          confirmButtonVariant={confirm.action === 'revoke' ? 'danger' : 'primary'}
          isLoading={act.isPending}
          onConfirm={() => act.mutate({ id: confirm.check.id, action: confirm.action })}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
};
